import React, { memo } from 'react';
import {
  MuiFormControl,
  MuiFormHelperText,
  MuiInputLabel,
  MuiMenuItem,
  MuiSelect,
} from '@/features/shared/material-ui';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { InputBaseComponentProps } from '@material-ui/core';
import { useStyles } from './MarketoSelect.styles';

interface SelectOption {
  value: string;
  label: string;
}

interface MarketoSelectProps {
  classes: {
    formControl: string;
    select: string;
    menuItem: string;
  };
  error?: string;
  disabled?: boolean;
  attributes: {
    id: string;
    name: string;
    'aria-invalid'?: string;
    [key: string]: any;
  };
  label?: string;
  value: Record<string, any>;
  onChange: (event: React.ChangeEvent<{ value: unknown }>) => void;
  selectIconClassName: string;
  options: SelectOption[];
}

const MarketoSelect: React.FC<MarketoSelectProps> = ({
  classes,
  error,
  disabled = false,
  attributes,
  label,
  value,
  onChange,
  selectIconClassName,
  options,
}) => {
  const styles = useStyles();
  const isInvalid = Boolean(error) || /true/.test(attributes['aria-invalid'] as string);
  const labelId = `${attributes.id}_label`;

  const renderExpandIcon = (props: any) => (
    <ExpandMoreIcon
      {...props}
      className={clsx(props.className, selectIconClassName)}
      fontSize="small"
    />
  );

  const renderMenuItem = (option: SelectOption, index: number) => (
    <MuiMenuItem
      key={option?.value || `option_${index}`}
      value={option?.value}
      className={clsx(classes.menuItem, styles.selectItem)}
      disabled={disabled}
    >
      {option?.label}
    </MuiMenuItem>
  );

  return (
    <MuiFormControl
      variant="outlined"
      size="small"
      fullWidth
      className={classes.formControl}
      error={isInvalid}
      disabled={disabled}
    >
      {label && <MuiInputLabel id={labelId}>{label}</MuiInputLabel>}
      <MuiSelect
        labelId={labelId}
        value={value[attributes.name]}
        onChange={onChange}
        label={label}
        className={classes.select}
        inputProps={attributes as Partial<InputBaseComponentProps>}
        IconComponent={renderExpandIcon}
        MenuProps={{
          classes: { paper: styles.selectPaper },
          disableScrollLock: true,
        }}
      >
        {options.map(renderMenuItem)}
      </MuiSelect>
      {error && <MuiFormHelperText>{error}</MuiFormHelperText>}
    </MuiFormControl>
  );
};

export default memo(MarketoSelect);
